import { ThemeOptions, ThemeProvider, createTheme } from '@mui/material';
import { merge } from 'lodash';
import { FC, ReactNode } from 'react';

import { tableComponentOverrides } from './components/tables/theming';
import {
    AXLE_PAGE_BACKGROUND_GRAY,
    AXLE_TEAL,
    DARK_AXLE_TEAL,
    LIGHT_AXLE_TEAL_BACKGROUND,
} from './constants/colors';
import { adjustBrightness } from './utils/colors';

//////////////////////////////////////////////////////////////////////////////
// Z-index.
//////////////////////////////////////////////////////////////////////////////

// Need this to just be above the other page elements.
export const PAGE_DATE_PICKER_Z_INDEX_BASE = 1200;
export const PAGE_DATE_PICKER_Z_INDEX_TOP = 1201;
// Need this to be higher than the date picker, as when the navigation drawer expands over the page,
//  if the drawer isn’t over the date picker.
export const NAVIGATION_DRAWER_Z_INDEX = 1202;
// Need this to be higher than navigation drawer, as it sort of covers it up.
export const APP_BAR_Z_INDEX = 1203;
// Pins the schedules' saved-filters-paused note above the calendar, but below the page
//  controls' menus and the app bar.
export const SCHEDULES_PAUSED_NOTE_Z_INDEX = 1000;
// Pins the schedules' controls above the paused note and the calendar.
export const SCHEDULES_STICKY_BAR_Z_INDEX = 1001;
// Need this to be above the app bar and navigation drawer, so it’s truly full screen
//  above everything. Need `!important` to overcome CSS specificity.
export const PAGE_DATE_PICKER_FULL_SCREEN_Z_INDEX = '1204 !important';

//////////////////////////////////////////////////////////////////////////////
// Alerts.
//////////////////////////////////////////////////////////////////////////////

// Centers an Alert's icon, message and action vertically. MUI places them by fixed top padding,
//  which only lines them up for its default font.
export const ALERT_CENTERED_CLASS = 'alert-centered';

//////////////////////////////////////////////////////////////////////////////
// Map markers.
//////////////////////////////////////////////////////////////////////////////

// Places a marker's bottom-center on its map coordinate.
export const MAP_MARKER_BOTTOM_ANCHOR_TRANSFORM = 'translate(-50%, -100%)';
// Places an MUI icon marker's tip on its map coordinate. The icon's glyph ends
//  above the bottom of its box, so the offset is less than the full height.
export const MAP_ICON_TIP_ANCHOR_TRANSFORM = 'translate(-50%, -75%)';

//////////////////////////////////////////////////////////////////////////////
// MUI theme.
//////////////////////////////////////////////////////////////////////////////
const defaultTheme = createTheme();

type NestedThemeProps = {
    options: ThemeOptions;
    children: ReactNode;
};

export const NestedTheme: FC<NestedThemeProps> = (props) => {
    const { options, children } = props;

    return (
        <ThemeProvider theme={(parentTheme) => createTheme(parentTheme, options)}>
            {children}
        </ThemeProvider>
    );
};

const commonTheme: ThemeOptions = {
    palette: {
        outline: {
            surface: '#FAFAFA',
            divider: '#E1E3E2',
            border: '#6F7978',
        },
    },
    shape: {
        borderRadius: 10,
    },
    components: {
        MuiAlert: {
            styleOverrides: {
                root: {
                    [`&.${ALERT_CENTERED_CLASS}`]: {
                        alignItems: 'center',
                        '& .MuiAlert-message, & .MuiAlert-action': {
                            paddingTop: 0,
                            paddingBottom: 0,
                        },
                    },
                },
                standardInfo: {
                    backgroundColor: LIGHT_AXLE_TEAL_BACKGROUND,
                    color: DARK_AXLE_TEAL,
                    border: `1px ${DARK_AXLE_TEAL} solid`,
                    '& svg': {
                        color: adjustBrightness(AXLE_TEAL, -50),
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    maxWidth: 500,
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    lineHeight: 1.5,
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgb(230, 230, 232, 0.4)',
                },
            },
        },
        MuiModal: {
            styleOverrides: {
                backdrop: {
                    backgroundColor: 'transparent',
                },
            },
        },
        ...tableComponentOverrides,
        // Spread bypasses excess property checking bc MuiPickersYear is from @mui/x-date-pickers
        ...{ MuiPickersYear: { styleOverrides: { yearButton: { fontSize: '1rem' } } } },
    },
    typography: {
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',\n" +
            "        'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans',\n" +
            "        'Helvetica Neue', sans-serif",
        h1: {
            fontSize: 28,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
        },
        h2: {
            fontSize: 20,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
        },
        h3: {
            fontSize: 16,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 0,
        },
        h4: {
            fontSize: 14,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 0,
            marginBottom: 0,
        },
        h5: {
            fontSize: 10,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
        },
        subtitle1: {
            fontSize: '24px',
            fontWeight: 'normal',
        },
        subtitle2: {
            fontSize: '16px',
            fontWeight: 'normal',
        },
        body1: {
            fontSize: 16,
            fontFamily: 'Roboto, sans-serif',
        },
        body2: {
            fontSize: 12,
            fontFamily: 'Roboto, sans-serif',
        },
    },
    breakpoints: {
        values: {
            ...defaultTheme.breakpoints.values,
            // The isXsDown boolean defines the smallest table width accessible via our theme's breakpoints, but its
            //  pixel value is 600px. The true mobile widths range from 430px for the iPhone 14 Pro Max, to the
            //  iPhone SE at 375px. We are hardcoding this phone width at 440px to ensure that the table is still
            //  legible on the smallest mobile devices.
            // Yatharth: Update: I am commenting this, as it was making the dialogs too thin,
            //  and the next size up was too wide.
            // sm: 440, // 'xs' will be considered stuff below this.
        },
    },
};

const createMergedTheme = (theme: ThemeOptions) => createTheme(merge(commonTheme, theme));

export const AxleTheme = createMergedTheme({
    palette: {
        primary: {
            main: '#54BBBD',
            contrastText: '#fff',
        },
        secondary: {
            main: '#303030',
        },
        background: {
            default: AXLE_PAGE_BACKGROUND_GRAY,
            paper: '#FDFDFD',
        },
    },
});

export const AxleV2Theme = createMergedTheme({
    palette: {
        primary: {
            main: '#007A75',
            container: '#003331',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#2E5D5B',
            container: '#D7E9E7',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#FFFFFF',
            paper: '#FDFDFD',
        },
        tertiary: {
            main: '#16665F',
            container: '#B3E4DE',
            contrastText: '#FAFAFA',
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 4,
                },
            },
        },
        // Match input border radius to buttons (4px) instead of the theme default (12px).
        // Covers `<TextField variant='outlined'>` (the codebase default), filled inputs,
        // and the select-style wrappers (Autocomplete / Select render through OutlinedInput).
        MuiOutlinedInput: {
            styleOverrides: { root: { borderRadius: 4 } },
        },
        MuiFilledInput: {
            styleOverrides: { root: { borderRadius: 4 } },
        },
    },
});
